import { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { searchArtistsByValue } from '../data/artists';
import { searchAlbumsByValue } from '../data/albums';
import { searchSinglesByValue } from '../data/singles';
import { searchGenresByValue } from '../data/musicGenres';
import SearchResultsSection from './SearchResultsSection';

const SearchBar = () => {

  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState({
    artists: [],
    albums: [],
    singles: [],
    genres: [],
  });
  const [showResults, setShowResults] = useState(false);

  const navigate = useNavigate();

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (value) {
      const filteredResults = {
        artists: searchArtistsByValue(value),
        albums: searchAlbumsByValue(value),
        singles: searchSinglesByValue(value),
        genres: searchGenresByValue(value),
      };
      setSearchResults(filteredResults);
    } else {
      setSearchResults({ artists: [], albums: [], singles: [], genres: [] });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchValue) {
      navigate(`/search/${searchValue}`);
    }
  };

  const handleResultClick = (type, id) => {
    switch (type) {
      case 'artist':
        navigate(`/artist/${id}`);
        break;
      case 'album':
        navigate(`/album/${id}`);
        break;
      case 'single':
        navigate(`/single/${id}`);
        break;
      case 'genre':
        const lowerCaseId = id.toLowerCase().split(' ').join('');
        navigate(`/genre/${lowerCaseId}`);
        break;
      default:
        break;
    }
    setShowResults(false);
  };

  return (
    <div className="relative w-full sm:max-w-[50%] lg:max-w-lg">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        className="block w-full p-2 pl-10 rounded-full bg-charcoal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
        placeholder="Search"
        value={searchValue}
        onChange={handleSearch}
        onKeyPress={handleKeyPress}
        onFocus={() => setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 200)}
      />
      {showResults && (
        <div className="absolute z-50 mt-2 w-full bg-charcoal rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {searchValue ? (
            <>
              {searchResults.artists.length > 0 && (
                <SearchResultsSection
                  title="Artists"
                  items={searchResults.artists}
                  type="artist"
                  handleResultClick={handleResultClick}
                  searchValue={searchValue}
                />
              )}
              {searchResults.albums.length > 0 && (
                <SearchResultsSection
                  title="Albums"
                  items={searchResults.albums}
                  type="album"
                  handleResultClick={handleResultClick}
                  searchValue={searchValue}
                />
              )}
              {searchResults.singles.length > 0 && (
                <SearchResultsSection
                  title="Singles"
                  items={searchResults.singles}
                  type="single"
                  handleResultClick={handleResultClick}
                  searchValue={searchValue}
                />
              )}
              {searchResults.genres.length > 0 && (
                <SearchResultsSection
                  title="Genres"
                  items={searchResults.genres}
                  type="genre"
                  handleResultClick={handleResultClick}
                  searchValue={searchValue}
                />
              )}
            </>
          ) : (
            <div className="p-2">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Recent Searches</h3>
              <div className="text-sm text-gray-300">No recent searches</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;